import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";
import { LangChangeEvent, TranslateModule, TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";

import { UnitOfMeasure } from "@app/core/enums/unit-of-measure";
import { ShipmentDutiableRow } from "@app/shared/CSDAModels/ShipmentDutiableRow";


export interface PidDialogData {
  products: ShipmentDutiableRow[];
  destinationCountryCode: string;
  unitOfMeasure: UnitOfMeasure;
  carrierName?: string;
  pidMandatory?: boolean;
  receiverVATRegistered?: boolean;
}


export interface PidProductResult {
  productIndex: number;
  sku: string;
  manufacturerPartNumber: string;
  gtinUpcEan: string;
}


export interface PidDialogResult {
  receiverVATRegistered: boolean;
  products: PidProductResult[];
}


interface PidProductRow {
  productIndex: number;
  product: ShipmentDutiableRow;
  sku: string;
  manufacturerPartNumber: string;
  gtinUpcEan: string;
}


@Component({
  selector: "app-pid",
  standalone: true,

  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    TranslateModule,
    MatTooltipModule
  ],

  templateUrl: "./pid.component.html",
  styleUrls: ["./pid.component.scss"]
})
export class PidComponent {

  receiverVATRegistered: boolean | null = null;

  products: PidProductRow[] = [];


  constructor(
    public dialogRef: MatDialogRef<PidComponent, PidDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: PidDialogData,
    private translateService: TranslateService
  ) {

    this.receiverVATRegistered = this.data.receiverVATRegistered ?? null;

    this.products = (this.data.products ?? []).map((product, index) => ({
      productIndex: index,
      product: product,
      sku: product.SKU ?? "",
      manufacturerPartNumber: product.ManufacturerPartNumber ?? "",
      gtinUpcEan: product.GTIN ?? ""
    }));

  }


  get unitOfMeasureTranslationKey(): string {

    return this.data.unitOfMeasure === UnitOfMeasure.Imperial
      ? "app.misc.imperialMeasure"
      : "app.misc.metricMesure";

  }


  get isDhl(): boolean { return this.data.carrierName?.toUpperCase() === "DHL"; }

  get pidRequired(): boolean { return this.receiverVATRegistered === false && this.data.pidMandatory !== false; }

  get pidEnabled(): boolean { return this.receiverVATRegistered === false; }

  get isFrench(): boolean { return this.translateService.currentLang?.toLowerCase().startsWith("fr") ?? false; }

  get dhlInformationTitle(): string { return this.isFrench ? "Informations supplémentaires pour DHL" : "Additional information for DHL"; }

  get dhlInformationDescription(): string { return this.isFrench ? "Les identifiants de produits sont disponibles pour les envois B2C de marchandises vers l’Union européenne, quel que soit leur montant." : "Product identifiers are available for B2C dutiable shipments to the European Union, regardless of shipment value."; }

  get informationDescription(): string { return this.isFrench ? "Pour cet envoi B2C vers l’Union européenne, veuillez fournir les identifiants de produits requis." : "For this B2C shipment to the European Union, please provide the required product identifiers."; }

  get warningTitle(): string { return this.isDhl ? this.dhlWarningTitle : (this.isFrench ? "Identifiants de produits FedEx" : "FedEx product identifiers"); }

  get warningText(): string { return this.isDhl ? this.dhlWarningText : (this.isFrench ? "Pour cet envoi B2C de 150 € ou moins, le SKU et le numéro de pièce du fabricant sont requis. Le GTIN / UPC / EAN est facultatif." : "For this B2C shipment valued at €150 or less, SKU and Manufacturer Part Number are required. GTIN / UPC / EAN is optional."); }

  get dhlWarningTitle(): string { return this.isFrench ? "Identifiants de produits DHL" : "DHL product identifiers"; }

  get dhlWarningText(): string { return this.pidRequired ? (this.isFrench ? "Pour cet envoi B2C de 150 € ou moins, le SKU et le numéro de pièce du fabricant sont requis. Le GTIN / UPC / EAN est facultatif." : "For this B2C shipment valued at €150 or less, SKU and Manufacturer Part Number are required. GTIN / UPC / EAN is optional.") : (this.isFrench ? "Ces identifiants peuvent être renseignés, mais ne sont pas obligatoires pour cet envoi." : "These identifiers can be provided but are not mandatory for this shipment."); }


  cancel(): void {
    this.dialogRef.close(null);
  }


  async confirm(): Promise<void> {

    if (this.pidRequired) {

      const invalidRowIndex = this.products.findIndex(product => !product.sku?.trim() || !product.manufacturerPartNumber?.trim());


      if (invalidRowIndex !== -1) {

        await Swal.fire({
          icon: "warning",
          title: this.translateService.instant("app.pid.missingProductIdentifierTitle"),
          html: this.translateService.instant("app.pid.missingProductIdentifierText") + "<br><br><strong>" + this.translateService.instant("app.pid.product") + " " + (invalidRowIndex + 1) + "</strong>",
          confirmButtonText: this.translateService.instant("app.pid.ok")
        });

        return;
      }

    }


    const result: PidDialogResult = {

      receiverVATRegistered: this.receiverVATRegistered,

      products: this.products.map(product => ({
        productIndex: product.productIndex,
        sku: product.sku?.trim() ?? "",
        manufacturerPartNumber: product.manufacturerPartNumber?.trim() ?? "",
        gtinUpcEan: product.gtinUpcEan?.trim() ?? ""
      }))

    };


    this.dialogRef.close(result);
  }

}